import toast from "react-hot-toast";
import { apiPaths } from "../../../../service/apiPaths";
import ApiService from "../../../../service/ApiService";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FormDataMain, IAddTestQuestion } from "../../TestQuestion.props";
import { SubmitHandler } from "react-hook-form";

interface FormData1 {
  [key: string]: string | number | any;
}

const useAddTestQuestion = () => {
  const [formData, setFormData] = useState<FormData1>({
    questions: [
      {
        questionText: "",
        description: "",
        level: "",
        subject: "",
        standard: "",
        courseName: "",
        languageName: "",
        options: [
          { optionText: "", isCorrect: false },
          { optionText: "", isCorrect: false },
          { optionText: "", isCorrect: false },
          { optionText: "", isCorrect: false },
        ],
        isLiveMcq: false,
      },
    ],
  });

  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const [courses, setCourses] = useState<any[]>([]);
  const [languages, setLanguages] = useState<any[]>([]);
  const [subjectName, setSubjectName] = useState<any[]>([]);
  const [standard, setStandard] = useState<any[]>([]);

useEffect(() => {
    fetchCoursesAndLanguages();
  }, []);

  
  const fetchCoursesAndLanguages = async () => {
    try {
      setLoading(true);
      const [
        coursesResponse,
        languagesResponse,
        subjectResponse,
        standardResponse,
      ] = await Promise.all([
        ApiService({ method: "GET", endpoint: apiPaths.getCourse }),
        ApiService({ method: "GET", endpoint: apiPaths.getlanguage }),
        ApiService({ method: "GET", endpoint: apiPaths.getSubject }),
        ApiService({ method: "GET", endpoint: apiPaths.getStandard }),
      ]);

      console.log(coursesResponse)
      setCourses(coursesResponse);
      setLanguages(languagesResponse);
      setSubjectName(subjectResponse);
      setStandard(standardResponse);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch data.");
    } finally {
      setLoading(false);
    }
  };


  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> | any,
    index?: number,
    optionIndex?: number
  ) => {
    const { name, value, type, checked } = e.target;

    console.log("E.target : ",name, value, type, checked )
    
  
    setFormData((prevState: any) => {
      const updatedQuestions = [...prevState.questions];

      if(name === "isLiveMcq") {
        updatedQuestions[index as number].isLiveMcq = checked;
        return { ...prevState, questions: updatedQuestions };
      }
  
      if (typeof index === "number") {
        if (typeof optionIndex === "number") {
          if (type === "checkbox") {
            // Unselect other options if any, ensuring only the current option is selected
            updatedQuestions[index].options = updatedQuestions[index].options.map((option: any, idx: number) => ({
              ...option,
              [name]: idx === optionIndex ? checked : false, // Only update the selected option to true, others to false
            }));
          } else {
            // For other input types, update the value directly
            updatedQuestions[index].options[optionIndex] = {
              ...updatedQuestions[index].options[optionIndex],
              [name]: type === "checkbox" ? checked : value,
            };
          }
          
        } else {
          // Update a field in a specific question
          updatedQuestions[index][name] = value;
        }
      } else {
        // Update formData fields that are not part of questions
        return {
          ...prevState,
          [name]: value,
        };
      }
      

      console.log("UPDATED Q : ",updatedQuestions)
      return { ...prevState, questions: updatedQuestions };
    });
  };
  

const handleSubmit = async () => {
  console.log("Form data before submitting : ", formData);

  // Check for duplicate options in any question
  const hasDuplicateOptions = formData.questions.some((question: any) => {
    const optionTexts = question.options
      .map((option: any) => option.optionText.trim().toLowerCase())
      .filter((text: string) => text !== ""); // Filter out empty options
    
    const uniqueTexts = new Set(optionTexts);
    return optionTexts.length !== uniqueTexts.size;
  });

  if (hasDuplicateOptions) {
    toast.error("Duplicate options found. Please ensure all options are unique for each question.");
    return;
  }

  try {
    await ApiService({
      method: "POST",
      endpoint: apiPaths.question,
      data: formData,
    });
    toast.success("Questions added successfully");
    
    // Reset ALL questions but keep their configuration fields (only on success)
    const resetQuestions = formData.questions.map((question: any) => ({
      // Keep configuration fields from each question
      level: question.level || "",
      subject: question.subject || "",
      standard: question.standard || "",
      courseName: question.courseName || "",
      languageName: question.languageName || "",
      isLiveMcq: question.isLiveMcq || false,
      
      // Reset content fields
      questionText: "",
      description: "",
      options: [
        { optionText: "", isCorrect: false },
        { optionText: "", isCorrect: false },
        { optionText: "", isCorrect: false },
        { optionText: "", isCorrect: false },
      ],
    }));

    // Update form data with reset questions (only on success)
    setFormData({
      questions: resetQuestions,
    });
  } catch (error) {
    console.error("Error adding questions:", error);
    toast.error("Error adding questions");
    // Don't reset form data on error - questions remain as they were
  }
};

const addQuestion = () => {
  // Validate all existing questions before adding a new one
  const isValidQuestion = (question:any) => {
    // Check if all required fields are filled (excluding isLiveMcq)
    const requiredFields = ['questionText', 'description', 'level', 'subject', 'standard', 'courseName', 'languageName'];
    const allFieldsFilled = requiredFields.every(field => question[field] && question[field].trim() !== '');
    
    // Check if all options have text
    const allOptionsHaveText = question.options.every((option:any) => option.optionText && option.optionText.trim() !== '');
    
    // Check if at least one option is marked as correct
    const hasCorrectOption = question.options.some((option:any) => option.isCorrect);
    
    return allFieldsFilled && allOptionsHaveText && hasCorrectOption;
  };

  // Check if all existing questions are valid
  const allQuestionsValid = formData.questions.every(isValidQuestion);
  
  if (!allQuestionsValid) {
    alert('Please fill all required fields and select at least one correct option for all existing questions before adding a new question.');
    return;
  }

  setFormData((prevState) => {
    const lastQuestion = prevState.questions[prevState.questions.length - 1];
    
    // Copy configuration fields from the last question
    const configurationFields = {
      level: lastQuestion?.level || "",
      subject: lastQuestion?.subject || "",
      standard: lastQuestion?.standard || "",
      courseName: lastQuestion?.courseName || "",
      languageName: lastQuestion?.languageName || "",
      isLiveMcq: lastQuestion?.isLiveMcq || false,
    };

    return {
      questions: [
        ...prevState.questions,
        {
          // Copy configuration from previous question
          ...configurationFields,
          // Reset content-specific fields
          questionText: "",
          description: "",
          options: [
            { optionText: "", isCorrect: false },
            { optionText: "", isCorrect: false },
            { optionText: "", isCorrect: false },
            { optionText: "", isCorrect: false },
          ],
        },
      ],
    };
  });
};
  
  const deleteQuestion = (index: number) => {
    if (formData.questions && formData.questions.length > 1) {
      const updatedQuestions = formData.questions.filter((_: any, i: number) => i !== index);
      setFormData((prevFormData: any) => ({
        ...prevFormData,
        questions: updatedQuestions,
      }));
      console.log("Form data after deleting question : ", updatedQuestions);
    }
  };

  const handleCancel=()=>{
    navigate("/add-question")
  }

  return {
    loading,
    courses,
    languages,
    subjectName,
    standard,
    formData,
    deleteQuestion,
    handleChange,
    handleSubmit,
    addQuestion,
    handleCancel,
  };
};

export default useAddTestQuestion;
